import React, { useState } from "react";
import { Formik, Form } from "formik";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

import Step1 from "./Step1";
import Step2 from "./Step2";
import useApi from "../../../shared/hooks/useApi";
import MainLogo from "../../../shared/components/Branding/MainLogo";


const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE, {
	advancedFraudSignals: false,
});

const parseDdMmYyyyToUtcDate = (value) => {
	if (!value || typeof value !== "string") return null;
	const trimmed = value.trim();

	// Support "DD-MM-YYYY" (primary) and "YYYY-MM-DD" (fallback)
	const ddmmyyyy = /^(\d{2})-(\d{2})-(\d{4})$/;
	const yyyymmdd = /^(\d{4})-(\d{2})-(\d{2})$/;

	let day;
	let month;
	let year;

	let match = trimmed.match(ddmmyyyy);
	if (match) {
		day = parseInt(match[1], 10);
		month = parseInt(match[2], 10);
		year = parseInt(match[3], 10);
	} else {
		match = trimmed.match(yyyymmdd);
		if (!match) return null;
		year = parseInt(match[1], 10);
		month = parseInt(match[2], 10);
		day = parseInt(match[3], 10);
	}

	if (
		Number.isNaN(day) ||
		Number.isNaN(month) ||
		Number.isNaN(year) ||
		day < 1 ||
		day > 31 ||
		month < 1 ||
		month > 12 ||
		year < 2000 ||
		year > 2100
	) {
		return null;
	}

	// Use UTC midnight to avoid timezone surprises
	const d = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
	return Number.isNaN(d.getTime()) ? null : d;
};

const computeTrialDaysUntilStartDate = (startDateStr) => {
	const start = parseDdMmYyyyToUtcDate(startDateStr);
	if (!start) return null;
	const nowMs = Date.now();
	const diffMs = start.getTime() - nowMs;
	const days = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
	if (Number.isNaN(days)) return null;
	// Keep consistent with backend max clamp
	return Math.max(0, Math.min(days, 365));
};

const SignupForm = ({ leadId, chain, coupon, type, startDate, prefill }) => {

	const location = useLocation();
	const [currentStep, setCurrentStep] = useState(1);
	const [clientSecret, setClientSecret] = useState("");
	const [isTransitioning, setIsTransitioning] = useState(false);
	const [planInfo, setPlanInfo] = useState(null);
	const [isChainSignup, setIsChainSignup] = useState(false);

	const {
		data: paymentIntentData,
		loading: paymentLoading,
		error: paymentError,
		execute: createPaymentIntent,
	} = useApi();

	const { execute: createYearlyCheckoutSession } = useApi();

	// Capture UTM parameters and referrer info
	const [trackingData] = useState(() => {
		try {
			const queryParams = new URLSearchParams(location.search || "");

			return {
				utm_source: queryParams.get("utm_source"),
				utm_medium: queryParams.get("utm_medium"),
				utm_campaign: queryParams.get("utm_campaign"),
				utm_term: queryParams.get("utm_term"),
				utm_content: queryParams.get("utm_content"),
				utm_id: queryParams.get("utm_id"),
				gclid: queryParams.get("gclid"), // Google Ads click ID
				fbclid: queryParams.get("fbclid"), // Facebook click ID
				referrer:
					(typeof document !== "undefined" && document.referrer) ||
					null,
				landing_page:
					(typeof window !== "undefined" && window.location?.href) ||
					null,
				user_agent:
					(typeof navigator !== "undefined" && navigator.userAgent) ||
					null,
				timestamp: new Date().toISOString(),
			};
		} catch (error) {
			console.warn("Error capturing tracking data:", error);
			// Return empty object if tracking data capture fails
			return {};
		}
	});

	const stepSchema = Yup.object({
		firstName: Yup.string().required("Voornaam is verplicht"),
		lastName: Yup.string().required("Achternaam is verplicht"),
		email: Yup.string()
			.email("Ongeldig e-mailadres")
			.required("E-mail is verplicht"),
		practiceName: Yup.string().required("Praktijknaam is verplicht"),
		address: Yup.string().required("Adres is verplicht"),
		city: Yup.string().required("Stad is verplicht"),
		postalCode: Yup.string().required("Postcode is verplicht"),
	});

	// Extract query parameters for prefilling Step 1 fields
	const queryParams = new URLSearchParams(location.search);
	const getQueryParam = (key, defaultValue = "") => {
		const value = queryParams.get(key);
		return value || defaultValue;
	};

	const flowType = type || queryParams.get("type"); // e.g. "payment_update"
	const startDateParam = startDate || queryParams.get("startDate"); // e.g. "12-01-2026"
	const isPaymentUpdate = flowType === "payment_update";

	// Read optional trialDays from URL (string), pass through to backend if present
	const trialDaysParamRaw = queryParams.get("trialDays");
	const derivedTrialDays =
		trialDaysParamRaw ??
		(isPaymentUpdate && startDateParam
			? String(computeTrialDaysUntilStartDate(startDateParam) ?? "")
			: null);
	const effectiveTrialDaysParam =
		typeof derivedTrialDays === "string" && derivedTrialDays !== ""
			? derivedTrialDays
			: null;
	// Optional coupon/promo code from URL or from parent prop (prop wins)
	const couponParam =
		coupon ||
		queryParams.get("coupon") ||
		queryParams.get("promo") ||
		queryParams.get("discount");

	const [initialValues] = useState(() => {
		const developmentDefaults =
			process.env.NODE_ENV === "development"
				? {
						firstName: "Matthijs",
						lastName: "Tolmeijer",
						email: "matthijs@tolmeijer.net",
						practiceName: "De Haagse Tandarts",
						address: "Kruislaan 10",
						city: "Den Haag",
						postalCode: "2512 EA",
						country: "Nederland",
					}
				: {};

		// Merge with query parameters for Step 1 fields only
		const queryDefaults = {
			firstName:
				(prefill?.firstName && String(prefill.firstName)) ||
				getQueryParam(
				"firstName",
				developmentDefaults.firstName,
			),
			lastName:
				(prefill?.lastName && String(prefill.lastName)) ||
				getQueryParam("lastName", developmentDefaults.lastName),
			email:
				(prefill?.email && String(prefill.email)) ||
				getQueryParam("email", developmentDefaults.email),
			practiceName:
				(prefill?.practiceName && String(prefill.practiceName)) ||
				getQueryParam(
				"practiceName",
				developmentDefaults.practiceName,
			),
			address:
				(prefill?.address && String(prefill.address)) ||
				getQueryParam("address", developmentDefaults.address),
			city:
				(prefill?.city && String(prefill.city)) ||
				getQueryParam("city", developmentDefaults.city),
			postalCode:
				(prefill?.postalCode && String(prefill.postalCode)) ||
				getQueryParam("postalCode", developmentDefaults.postalCode),
			country:
				(prefill?.country && String(prefill.country)) ||
				getQueryParam("country", developmentDefaults.country) ||
				"Nederland",
		};

		return {
			address: "",
			city: "",
			postalCode: "",
			country: "Nederland",
			...developmentDefaults,
			...queryDefaults,
		};
	});

	// Track landing on signup
	React.useEffect(() => {
		try {
			if (
				typeof window !== "undefined" &&
				typeof window.datafast === "function"
			) {
				window.datafast("start_signup", {
					leadId: leadId || null,
					chain: chain || null,
					path:
						typeof window !== "undefined"
							? window.location?.pathname
							: null,
					...trackingData,
				});
			}
		} catch (_) {}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleStep1Submit = async (values) => {
		setIsTransitioning(true);

		// Track step 1 submit and identify
		try {
			if (
				typeof window !== "undefined" &&
				typeof window.datafast === "function"
			) {
				window.datafast("signup_step1_submitted", {
					practiceName: values.practiceName,
					coupon: couponParam || null,
					trialDays:
						effectiveTrialDaysParam !== null
							? effectiveTrialDaysParam
							: null,
					type: flowType || null,
					startDate: startDateParam || null,
				});
				window.datafast("identify", {
					practiceName: values.practiceName,
					email: values.email,
				});
			}
		} catch (_) {}

		// Decide between direct Checkout (no trial) vs SetupIntent flow
		const parsedTrialDays =
			typeof effectiveTrialDaysParam === "string" &&
			effectiveTrialDaysParam !== ""
				? parseInt(effectiveTrialDaysParam, 10)
				: 0;

		// If there is no trial, redirect directly to Stripe Checkout for full upfront payment
		if (
			!isPaymentUpdate &&
			!Number.isNaN(parsedTrialDays) &&
			parsedTrialDays === 0
		) {
			const checkoutResponse = await createYearlyCheckoutSession(
				"/api/billing/create-yearly-checkout-session",
				{
					method: "POST",
					body: {
						currency: "eur",
						metadata: {
							firstName: values.firstName,
							lastName: values.lastName,
							email: values.email,
							practiceName: values.practiceName,
							address: values.address,
							city: values.city,
							postalCode: values.postalCode,
							country: values.country || "Nederland",
							...(couponParam && { coupon: couponParam }),
							...(leadId && { uid: leadId }),
							...(chain && { chain }),
							...(flowType && { type: flowType }),
							...(startDateParam && { startDate: startDateParam }),
							...trackingData,
						},
					},
					requireAuth: false,
				},
			);

			// On success, redirect to Stripe-hosted Checkout page
			if (checkoutResponse && checkoutResponse.url) {
				try {
					if (
						typeof window !== "undefined" &&
						typeof window.datafast === "function"
					) {
						window.datafast("redirect_to_stripe_checkout", {
							trialDays: 0,
							coupon: couponParam || null,
						});
					}
				} catch (_) {}

				window.location.href = checkoutResponse.url;
				return;
			}

			// Fallback: stop transitioning if something went wrong
			setIsTransitioning(false);
			return;
		}

		// With a trial: keep existing SetupIntent + PaymentElement flow
		await createPaymentIntent("/api/billing/create-payment-intent", {
			method: "POST",
			body: {
				currency: "eur",
				metadata: {
					firstName: values.firstName,
					lastName: values.lastName,
					email: values.email,
					practiceName: values.practiceName,
					address: values.address,
					city: values.city,
					postalCode: values.postalCode,
					country: values.country || "Nederland",
					...(couponParam && { coupon: couponParam }),
					// Forward trialDays from URL if provided. Keep as string for Stripe metadata.
					...(effectiveTrialDaysParam !== null && {
						trialDays: effectiveTrialDaysParam,
					}),
					...(flowType && { type: flowType }),
					...(startDateParam && { startDate: startDateParam }),
					...(leadId && { uid: leadId }),
					...(chain && { chain }),
					...trackingData, // Include UTM parameters and referrer info
				},
			},
			requireAuth: false,
		});
	};

	React.useEffect(() => {
		if (paymentIntentData && paymentIntentData.clientSecret) {
			setClientSecret(paymentIntentData.clientSecret);
			if (paymentIntentData.planInfo) {
				setPlanInfo(paymentIntentData.planInfo);
			}
			// Track payment intent received and moving to step 2
			try {
				if (
					typeof window !== "undefined" &&
					typeof window.datafast === "function"
				) {
					window.datafast("payment_intent_received", {
						planName: paymentIntentData?.planInfo?.name || null,
						trialDays:
							paymentIntentData?.planInfo?.trialDays ?? null,
						coupon: couponParam || null,
					});
					window.datafast("signup_step_change", { step: 2 });
				}
			} catch (_) {}
			setTimeout(() => {
				setCurrentStep(2);
				setIsTransitioning(false);
			}, 300);
		} else if (paymentIntentData && paymentIntentData.chainSignup) {
			// For chain signups, show processing message and redirect to success page
			setIsChainSignup(true);
			try {
				if (
					typeof window !== "undefined" &&
					typeof window.datafast === "function"
				) {
					window.datafast("chain_signup_initiated", {
						chain: chain || null,
					});
				}
			} catch (_) {}
			setTimeout(() => {
				window.location.href = "/betaling/succes";
			}, 2000);
		}
	}, [paymentIntentData, chain, couponParam]);

	const handleBackToStep1 = () => {
		setIsTransitioning(true);
		try {
			if (
				typeof window !== "undefined" &&
				typeof window.datafast === "function"
			) {
				window.datafast("signup_step_change", { step: 1 });
			}
		} catch (_) {}
		setTimeout(() => {
			setCurrentStep(1);
			setIsTransitioning(false);
		}, 150);
	};

	const appearance = {
		theme: "stripe",
		variables: {
			colorPrimary: "#7634DE",
			colorBackground: "#ffffff",
			colorText: "#1f2937",
			colorDanger: "#ef4444",
			fontFamily: "system-ui, sans-serif",
			spacingUnit: "4px",
			borderRadius: "12px",
		},
	};

	const options = {
		clientSecret,
		appearance,
	};

	return (
		<div className="min-h-screen w-full flex flex-col bg-slate-50">
			<div className="w-full py-6">
				<div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between w-full px-3 sm:px-0">
					<div className="flex items-center w-full sm:w-auto justify-between">
						<MainLogo />
					</div>
					<div className="flex-col sm:flex-row items-center justify-center w-full sm:w-auto mt-3 sm:mt-0 hidden sm:flex">
						<nav
							aria-label="Progress"
							className="flex items-center gap-x-2 w-full sm:w-auto"
						>
							<p className="text-xs sm:text-sm font-semibold text-blue-600 text-right pr-2 w-20 sm:w-24">
								Stap {currentStep}/2
							</p>
							<ol className="flex items-center w-full gap-x-1 sm:gap-x-2">
								<div
									className={`h-2 w-14 sm:w-20 rounded-full transition-all duration-300 ease-in-out ${currentStep >= 2 ? "bg-blue-600" : currentStep >= 1 ? "bg-blue-600" : "bg-slate-200"}`}
								></div>
								<div
									className={`h-2 w-14 sm:w-20 rounded-full transition-all duration-300 ease-in-out ${currentStep >= 2 ? "bg-purple-600" : "bg-slate-200"}`}
								></div>
							</ol>
						</nav>
					</div>
				</div>
			</div>
			<div className="flex flex-1 items-stretch justify-center px-4 py-10">
				<div className="max-w-6xl w-full">
					<div className="w-full relative z-10">
						<Formik
							initialValues={initialValues}
							validationSchema={stepSchema}
							onSubmit={
								currentStep === 1 ? handleStep1Submit : () => {}
							}
							validateOnChange={true}
							validateOnBlur={true}
							enableReinitialize={true}
						>
							{(formikProps) => (
								<Form>
									<div className="relative">
										<div
											className={`
												transition-all duration-500 ease-out transform
												${
													currentStep === 1
														? "translate-x-0 opacity-100"
														: "translate-x-[-100%] opacity-0 absolute inset-0"
												}
												${isTransitioning ? "pointer-events-none" : ""}
											`}
										>
											{currentStep === 1 && (
												<Step1
													formikProps={formikProps}
													isLoading={
														paymentLoading
													}
													coupon={couponParam}
													trialDays={
														effectiveTrialDaysParam
													}
													isPaymentUpdate={
														isPaymentUpdate
													}
													error={
														paymentError
													}
												/>
											)}
										</div>
										{/* Step 2 */}
										<div
											className={`
												transition-all duration-500 ease-out transform
												${
													currentStep === 2
														? "translate-x-0 opacity-100"
														: "translate-x-full opacity-0 absolute inset-0"
												}
												${isTransitioning ? "pointer-events-none" : ""}
											`}
										>
											{currentStep === 2 &&
												clientSecret && (
													<Elements
														stripe={stripePromise}
														options={options}
													>
														<Step2
															formikProps={
																formikProps
															}
															clientSecret={
																clientSecret
															}
															onBack={
																handleBackToStep1
															}
															planInfo={planInfo}
														/>
													</Elements>
												)}
										</div>
										{/* Loading overlay for transitions */}
										{(isTransitioning || isChainSignup) && (
											<div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-20">
												<div className="flex items-center space-x-3">
													<div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent"></div>
													<span className="text-purple-700 font-medium">
														{isChainSignup
															? `Account aanmaken voor ${chain}...`
												: "Even geduld..."}
													</span>
												</div>
											</div>
										)}
									</div>
								</Form>
							)}
						</Formik>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SignupForm;
